import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useTranslation } from "./TranslationContext";
import AuthForms from "../components/auth/AuthForms";
import Modal from "../components/ds/Modal";
import { isPublicPath } from "../utils/publicRoutes";

/*
  The sign-in popup. Signed-out visitors can browse Discover, the blog, About us
  and product pages; anything else opens this popup over the page they are on,
  and once they are in it carries on to where they were heading.
*/

const AuthModalContext = createContext(null);

export const AuthModalProvider = ({ isDark, setToast, children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // { form, redirectTo }
  const lastPublicPath = useRef(null);

  useEffect(() => {
    if (isPublicPath(location.pathname)) {
      lastPublicPath.current = `${location.pathname}${location.search}`;
    }
  }, [location.pathname, location.search]);

  const openAuthModal = useCallback(({ redirectTo = null, form = "signup" } = {}) => {
    setModal({ redirectTo, form });
  }, []);

  const closeAuthModal = useCallback(() => setModal(null), []);

  // Reaching the full-page sign-in (e.g. after logging out) drops a pending popup.
  useEffect(() => {
    if (location.pathname === "/auth") setModal(null);
  }, [location.pathname]);

  const handleAuthenticated = useCallback(() => {
    const target = modal?.redirectTo;
    setModal(null);
    if (target && target !== `${window.location.pathname}${window.location.search}`) {
      navigate(target);
    }
  }, [modal, navigate]);

  const value = useMemo(
    () => ({ openAuthModal, closeAuthModal, lastPublicPath }),
    [openAuthModal, closeAuthModal]
  );

  // The full-page sign-in already shows the same forms.
  const open = Boolean(modal) && location.pathname !== "/auth";

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onClose={closeAuthModal}
        title={t("auth.popupTitle")}
        description={t("auth.popupDescription")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        {modal && (
          <AuthForms
            isDark={isDark}
            setToast={setToast}
            activeForm={modal.form}
            onFormChange={(form) => setModal((current) => current && { ...current, form })}
            redirectTo={modal.redirectTo}
            onAuthenticated={handleAuthenticated}
          />
        )}
      </Modal>
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => useContext(AuthModalContext);

/**
 * Drop-in for useNavigate on pages a signed-out visitor can see: going to a
 * page that needs an account opens the sign-in popup instead, and the
 * visitor lands there once signed in.
 */
export const useGuardedNavigate = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();

  return useCallback(
    (to, options) => {
      const path = typeof to === "string" ? to : to && typeof to === "object" ? `${to.pathname || ""}${to.search || ""}` : "";
      // Relative paths and history steps (-1) pass through; ProtectedRoute still catches them.
      if (!isAuthenticated && path.startsWith("/") && !isPublicPath(path)) {
        openAuthModal({ redirectTo: path });
        return;
      }
      navigate(to, options);
    },
    [navigate, isAuthenticated, openAuthModal]
  );
};
